/**
 * Sales Taxes and Charges, and the additional discount's interaction with them.
 *
 * The rules and the worked example are Akhil's (2026-09-04); the arithmetic is pinned against his
 * two stated totals — 1,207.91 and 1,201.41 — in this module's test, so a change that breaks
 * either fails loudly rather than quietly repricing every quotation.
 *
 * `Quotation.taxes` is a live `Sales Taxes and Charges` child table and `charge_type` is a Select
 * of exactly the five values below (verified against the doctype).
 */

const round2 = (n: number): number => Math.round(n * 100) / 100
const num = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) ? n : 0)

/**
 * Exactly the doctype's Select values. Declared in `types.ts` and re-exported here so the engine
 * and the screen cannot drift onto two different vocabularies.
 */
export type { ChargeType } from './types'
import type { ChargeType } from './types'

export interface TaxRow {
  /** 1-based, as Frappe numbers child rows. `row_id` references this. */
  idx: number
  charge_type: ChargeType
  account_head?: string
  tax_rate?: number
  /** Only meaningful for `Actual`, where it is typed rather than computed. */
  tax_amount?: number
  /** The row a "Previous Row" charge refers to, as a string — Frappe stores it that way. */
  row_id?: string
}

export interface ComputedTaxRow extends TaxRow {
  tax_amount: number
  /** Net (post-discount where that applies) plus every tax up to and including this row. */
  running_total: number
  /**
   * ERPNext's `tax_amount_after_discount_amount` (2026-09-17): the row recomputed on the net
   * AFTER the discount. Equal to `tax_amount` on Net Total (the displayed amount is already the
   * after-discount one) and with no discount; smaller on Grand Total for a percentage row.
   */
  tax_amount_after_discount: number
  /** ERPNext's row `total`: net after discount plus the after-discount tax accumulated so far. */
  total_after_discount: number
}

export type DiscountBasis = 'Grand Total' | 'Net Total'

export interface DiscountInput {
  applyOn: DiscountBasis
  mode: 'pct' | 'amount'
  value: number
}

export interface QuotationTotalsResult {
  taxes: ComputedTaxRow[]
  totalTax: number
  /** The net the taxes were actually computed against — reduced in `Net Total` mode. */
  netAfterDiscount: number
  discountAmount: number
  grandTotal: number
  /**
   * ERPNext's stored `net_total` once a discount is applied (`apply_discount_amount`): on Net
   * Total, net − discount; on Grand Total, net less its SHARE of the discount, the share being
   * net / (grand − flat charges) — the flat charges are not discounted, the goods and the
   * percentage taxes are. `netAfterDiscount` above is what the taxes were computed on.
   */
  netTotalAfterDiscount: number
}

/**
 * One pass down the rows, carrying the running total.
 *
 * Order matters and is the caller's: "Previous Row" charges chain off rows above them, so the
 * sequence the user arranged is the sequence that computes.
 */
function computeRows(rows: readonly TaxRow[], taxableNet: number, totalQty: number): ComputedTaxRow[] {
  const out: ComputedTaxRow[] = []
  let running = taxableNet

  for (const row of rows) {
    const pct = num(row.tax_rate) / 100
    let amount = 0

    switch (row.charge_type) {
      case 'Actual':
        // Typed, never calculated — the rate is ignored entirely for this type.
        amount = num(row.tax_amount)
        break
      case 'On Net Total':
        amount = taxableNet * pct
        break
      case 'On Item Quantity':
        amount = num(row.tax_rate) * totalQty
        break
      case 'On Previous Row Amount': {
        const ref = out.find((r) => String(r.idx) === String(row.row_id))
        // A reference to a row that is not there cannot be computed. Zero rather than falling back
        // to the net total, which would turn a broken reference into a plausible-looking charge.
        amount = ref ? ref.tax_amount * pct : 0
        break
      }
      case 'On Previous Row Total': {
        const ref = out.find((r) => String(r.idx) === String(row.row_id))
        amount = ref ? ref.running_total * pct : 0
        break
      }
    }

    amount = round2(amount)
    running = round2(running + amount)
    out.push({ ...row, tax_amount: amount, running_total: running, tax_amount_after_discount: amount, total_after_discount: running })
  }

  return out
}

/** The rows' after-discount side, recomputed on `netAfter` — ERPNext's second `_calculate` pass. */
function withAfterDiscount(rows: readonly ComputedTaxRow[], netAfter: number, totalQty: number): ComputedTaxRow[] {
  const again = computeRows(rows, netAfter, totalQty)
  return rows.map((row, i) => ({ ...row, tax_amount_after_discount: again[i].tax_amount, total_after_discount: again[i].running_total }))
}

/**
 * The whole totals calculation, both discount modes.
 *
 * ---- Why the mode changes more than the timing ----
 *
 *   `Net Total`   the discount comes off the net BEFORE the rows are computed, so every
 *                 percentage charge is calculated on the smaller base. The discount reduces the
 *                 taxable value — a genuine price reduction.
 *   `Grand Total` the rows are computed on the FULL net, and the discount comes off the finished
 *                 total. Tax is due on the undiscounted value; the discount reduces only what the
 *                 customer pays.
 *
 * The same percentage therefore yields two different totals, and Grand Total gives the customer
 * more — it discounts the flat charges and the tax as well, not merely the goods.
 */
export function computeQuotationTotals(input: {
  netTotal: number
  totalQty: number
  taxes: readonly TaxRow[]
  discount: DiscountInput | null
}): QuotationTotalsResult {
  const net = round2(num(input.netTotal))
  const discount = input.discount
  const onNet = discount?.applyOn === 'Net Total'

  // In `Net Total` mode the discount is known before the rows run, because it is taken off the
  // base they are computed against.
  const netDiscount =
    discount && onNet
      ? round2(discount.mode === 'pct' ? (net * Math.max(0, num(discount.value))) / 100 : Math.max(0, num(discount.value)))
      : 0
  const cappedNetDiscount = Math.min(netDiscount, net)
  const taxableNet = round2(net - cappedNetDiscount)

  const taxes = computeRows(input.taxes, taxableNet, num(input.totalQty))
  const totalTax = round2(taxes.reduce((sum, r) => sum + r.tax_amount, 0))
  const beforeDiscount = round2(taxableNet + totalTax)

  if (!discount || onNet) {
    return {
      taxes,
      totalTax,
      netAfterDiscount: taxableNet,
      discountAmount: cappedNetDiscount,
      grandTotal: Math.max(0, beforeDiscount),
      // On Net Total the taxes were already computed on the reduced net: after = displayed.
      netTotalAfterDiscount: taxableNet,
    }
  }

  // `Grand Total` mode: the finished total is the base.
  const raw =
    discount.mode === 'pct'
      ? (beforeDiscount * Math.max(0, num(discount.value))) / 100
      : Math.max(0, num(discount.value))
  // Capped: a discount larger than the total would invert the invoice.
  const grandDiscount = round2(Math.min(raw, beforeDiscount))

  /*
    ERPNext distributes a Grand-Total discount over the items in proportion to the net, with the
    flat charges (Actual, On Item Quantity) left OUT of the base — `get_total_for_discount_amount`
    — then recomputes; `grand_total_diff` makes the grand total exactly before − discount, which
    is what is returned here. The reduced net and the rows' after-discount amounts are what the
    stored document carries.
  */
  const flatCharges = taxes.filter((r) => r.charge_type === 'Actual' || r.charge_type === 'On Item Quantity').reduce((sum, r) => sum + r.tax_amount, 0)
  const totalForDiscount = round2(beforeDiscount - flatCharges)
  const netTotalAfterDiscount = totalForDiscount > 0 ? round2(net - (grandDiscount * net) / totalForDiscount) : net
  return {
    taxes: withAfterDiscount(taxes, netTotalAfterDiscount, num(input.totalQty)),
    totalTax,
    netAfterDiscount: net,
    discountAmount: grandDiscount,
    grandTotal: round2(Math.max(0, beforeDiscount - grandDiscount)),
    netTotalAfterDiscount,
  }
}
